import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { topupCheckout, cryptoTopupCheckout, formatUSD } from "@/lib/apiClient";
import { toast } from "sonner";
import { Loader2, Wallet, CreditCard, Bitcoin } from "lucide-react";

const PACKS = [
  { id: "pack_5k", amount: 5000 },
  { id: "pack_25k", amount: 25000 },
  { id: "pack_100k", amount: 100000 },
];

export default function WalletModal({ open, onOpenChange }) {
  const { user } = useAuth();
  const [custom, setCustom] = useState("");
  const [loading, setLoading] = useState(false);
  const [method, setMethod] = useState("card");

  const go = async (payload) => {
    setLoading(true);
    try {
      const fn = method === "crypto" ? cryptoTopupCheckout : topupCheckout;
      const { checkout_url } = await fn({ ...payload, origin_url: window.location.origin });
      window.location.href = checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start top-up.");
      setLoading(false);
    }
  };

  const customAmount = parseFloat(custom);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="wallet-modal" className="bg-surface border border-gold/25 text-ink max-w-md p-0 gap-0 rounded-2xl overflow-hidden">
        <div className="p-8">
          <DialogHeader className="text-left space-y-2 mb-6">
            <div className="overline flex items-center gap-2"><Wallet size={13} /> Wallet</div>
            <DialogTitle className="font-serif text-4xl tracking-tight text-ink">Top up balance</DialogTitle>
            <DialogDescription className="text-ink-dim text-sm">
              Current balance: <span className="text-gold font-mono" data-testid="wallet-modal-balance">{formatUSD(user?.wallet_balance)}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-2 mb-6" data-testid="topup-method-toggle">
            <button onClick={() => setMethod("card")} data-testid="method-card"
              className={`flex items-center justify-center gap-2 rounded-lg py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] border transition-colors ${method === "card" ? "bg-gold text-bg border-gold" : "text-ink-dim border-white/10 hover:border-gold/50"}`}>
              <CreditCard size={14} /> Card
            </button>
            <button onClick={() => setMethod("crypto")} data-testid="method-crypto"
              className={`flex items-center justify-center gap-2 rounded-lg py-2.5 font-mono text-[11px] uppercase tracking-[0.15em] border transition-colors ${method === "crypto" ? "bg-gold text-bg border-gold" : "text-ink-dim border-white/10 hover:border-gold/50"}`}>
              <Bitcoin size={14} /> Crypto
            </button>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-5">
            {PACKS.map((p) => (
              <button
                key={p.id}
                disabled={loading}
                onClick={() => go({ package_id: p.id })}
                data-testid={`topup-${p.id}`}
                className="border border-white/10 rounded-xl py-4 hover:border-gold hover:bg-gold/5 transition-colors disabled:opacity-50"
              >
                <div className="font-mono text-lg text-gold">{formatUSD(p.amount)}</div>
              </button>
            ))}
          </div>

          <div className="flex items-center gap-4 mb-5">
            <span className="h-px flex-1 bg-[var(--line)]" />
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-dim">custom</span>
            <span className="h-px flex-1 bg-[var(--line)]" />
          </div>

          <div className="flex gap-2">
            <input
              type="number"
              value={custom}
              onChange={(e) => setCustom(e.target.value)}
              placeholder="Enter amount (USD)"
              data-testid="topup-custom-input"
              className="flex-1 bg-[var(--field-bg-soft)] border border-line rounded-lg px-4 py-3 font-mono text-sm text-ink focus:border-gold focus:outline-none placeholder:text-ink-dim"
            />
            <button
              disabled={loading || !(customAmount >= 100)}
              onClick={() => go({ amount: customAmount })}
              data-testid="topup-custom-btn"
              className="bg-gold text-bg font-sans font-bold text-sm px-6 rounded-lg hover:shadow-[0_0_24px_rgba(212,175,55,0.4)] transition-shadow disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? <Loader2 size={15} className="animate-spin" /> : "Top up"}
            </button>
          </div>
          <p className="mt-3 text-xs text-ink-dim">Minimum $100. {method === "crypto" ? "Pay with BTC, ETH, USDT & more via NOWPayments." : "Secure checkout via Stripe. Test card: 4242 4242 4242 4242."}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
